import axios from 'axios';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Modal from '@mui/material/Modal';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import React from 'react';
import PropTypes from 'prop-types';

import CustomButton from '../CustomButton';
import Mac from '../../assets/Mac';
import Windows from '../../assets/Windows';
import { getFormErrors } from '../../modules/formErrors';
import { trackGoal } from '../../modules/matomo';
import schema from '../../schema/support';

import Loading from '../Loading';

const Content = React.lazy(() => import('./Content'));

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;

const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ModalContent = styled(Box)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: '90vh',
  position: 'relative',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[10],
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: '90%',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  '.teamviewerContainer': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  [theme.breakpoints.up('sm')]: {
    '.formContainer': {
      marginRight: '5%',
    },
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    maxWidth: '80vw',
    '.formContainer': {
      // marginLeft: '36%',
    },
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '64vw',
    backgroundPosition: 'center',
    '.formContainer': {
      // marginLeft: '54%',
    },
  },
  [theme.breakpoints.up('xl')]: {
    maxWidth: '50vw',
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  color: theme.palette.contrastText,
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  fontSize: '1.5vw',
  '.MuiAlert-icon': {
    fontSize: '2.5vw',
  },
}));

const SupportModal = ({ open, onClose }) => {
  const [sent, setSent] = React.useState(false);
  const theme = useTheme();

  const handleSubmit = async (values, actions) => {
    try {
      await axios.post('/api/support', values);
      actions.resetForm();
      setSent(true);
      trackGoal(3);
    } catch (e) {
      actions.setErrors(getFormErrors(
        e,
        Object.keys(schema.initialValues),
        'Die Supportanfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
      ));
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSent(false);
    onClose();
  }

  return (
    <StyledModal
      open={open}
      onClose={handleClose}
    >
      <ModalContent
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-modal-title"
        aria-describedby="support-modal-description"
      >
        <Tooltip title="Fenster schließen">
          <CloseButton onClick={handleClose} aria-label="schließen">
            <CloseIcon />
          </CloseButton>
        </Tooltip>

        <Box className="teamviewerContainer">

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {/* <SupportAgentIcon sx={{ fontSize: '2.5rem' }} /> */}
            <Typography id="support-modal-title" variant="h5" component="h2">
              Remote Anwenderunterstützung
            </Typography>
          </Box>

          <Box id="support-modal-description" sx={{ mb: 3 }}>
            <p>
              Laden Sie <strong>Teamviewer QuickSupport</strong> herunter, um eine Remote-Sitzung zu starten.
            </p>
            <p>
              Teilen Sie uns anschließend ihre <strong>Sitzungs-ID</strong> mit,
              damit wir remote auf Ihren Computer zugreifen können. 
              Sie können das nebenstehende Formular verwenden oder uns unter der 
              <strong> 0611 300299</strong> direkt anrufen.
            </p>
          </Box>

          <img src="/assets/img/teamviewer.svg" alt="TeamViewer Logo" style={{ height: '2.5rem' }} />
          <Button
            aria-label="TeamViewer QuickSupport für Windows herunterladen (EXE, 33,5 MB, öffnet in neuem Fenster)"
            variant="contained"
            component={Link}
            href="/downloads/TeamViewerQS_x64.exe"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#fff',
              backgroundColor: '#096fd2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              marginTop: theme.spacing(2),
            }}
          >
            <Windows sx={{ mr: 1 }} />
            QuickSupport für Windows herunterladen (EXE, 33,5 MB)
            <DownloadIcon sx={{ ml: 1 }} />
          </Button>
          <Button
            aria-label="TeamViewer QuickSupport für macOS herunterladen (DMG, 73,2 MB, öffnet in neuem Fenster)"
            variant="contained"
            component={Link}
            href="/downloads/TeamViewerQS.dmg"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#fff',
              backgroundColor: '#096fd2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              marginTop: theme.spacing(2),
            }}
          >
            <Mac sx={{ mr: 1 }} />
            QuickSupport für macOS herunterladen (DMG, 73,2 MB)
            <DownloadIcon sx={{ ml: 1 }} />
          </Button>

        </Box>

        <Divider orientation="vertical" variant="middle" flexItem>
          <Typography variant="caption" color="text.secondary">
            <SupportAgentIcon sx={{ fontSize: '5rem' }} />
          </Typography>
        </Divider>

        <Box className="formContainer" sx={{ position: 'relative'}}>

          {sent ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2 }}>
              <StyledAlert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
                <strong>
                  Ihre Supportanfrage ist erfolgreich eingegangen!
                </strong>
                <p>
                  Wir werden Sie so schnell wie möglich zurückrufen.
                </p>
              </StyledAlert>
              <CustomButton
                fullWidth
                onClick={handleClose}
                sx={{ mt: 1 }}
              >
                Schließen
              </CustomButton>
            </Box>
          ) : (
            <React.Suspense fallback={<Loading />}>
              <Content handleSubmit={handleSubmit} />
            </React.Suspense>
          )}
        </Box>
      </ModalContent>
    </StyledModal>
  );
};

SupportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SupportModal;
